import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import api from "../api";
import logo from "../assets/zen-logo.png";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(useGSAP, SplitText);
import moonEarthCinematic from "../assets/moon-earth-cinematic.jpeg";

// ============ LOGIN PAGE ============
function LoginStarfield() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const motionOff = document.documentElement.classList.contains("zen-motion-off");
    let w = 0, h = 0;
    const resize = () => { w = canvas.width = window.innerWidth; h = canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);

    const tint = (name, a) => {
      if (name === "cyan") return `rgba(165,243,252,${a})`;
      if (name === "violet") return `rgba(214,188,250,${a})`;
      return `rgba(255,255,255,${a})`;
    };
    const pickTint = () => (Math.random() < 0.08 ? "cyan" : Math.random() < 0.14 ? "violet" : "white");

    const makeLayer = (count, sizeRange, alphaRange, speed) => ({
      speed,
      stars: Array.from({ length: count }, () => ({
        x: Math.random(), y: Math.random(),
        r: sizeRange[0] + Math.random() * (sizeRange[1] - sizeRange[0]),
        baseAlpha: alphaRange[0] + Math.random() * (alphaRange[1] - alphaRange[0]),
        phase: Math.random() * Math.PI * 2,
        freq: 0.4 + Math.random() * 1.2,
        tint: pickTint(),
      })),
    });
    // far / mid / near — derinlik hissi için farklı boyut+parlaklık+kayma hızı
    const layers = [
      makeLayer(150, [0.35, 1.0], [0.2, 0.5], 0.00030),
      makeLayer(80, [0.8, 1.6], [0.35, 0.7], 0.00065),
      makeLayer(26, [1.3, 2.3], [0.55, 0.9], 0.00110),
    ];

    let raf = null;

    let last = performance.now();
    let elapsed = 0;

    const draw = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      if (!motionOff) elapsed += dt;
      ctx.clearRect(0, 0, w, h);

      layers.forEach((layer) => {
        layer.stars.forEach((s) => {
          if (!motionOff) {
            s.x += layer.speed * dt;
            if (s.x > 1) s.x -= 1;
            if (s.x < 0) s.x += 1;
          }
          const twinkle = 0.55 + 0.45 * Math.sin(elapsed * s.freq + s.phase);
          const alpha = s.baseAlpha * twinkle;
          const px = s.x * w, py = s.y * h;
          ctx.beginPath();
          ctx.fillStyle = tint(s.tint, alpha);
          ctx.arc(px, py, s.r, 0, Math.PI * 2);
          ctx.fill();
          if (s.r > 1.3) {
            ctx.beginPath();
            ctx.fillStyle = tint(s.tint, alpha * 0.16);
            ctx.arc(px, py, s.r * 4.5, 0, Math.PI * 2);
            ctx.fill();
          }
        });
      });

      if (!motionOff) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    const onVis = () => {
      if (motionOff) return;
      if (document.hidden) { if (raf) cancelAnimationFrame(raf); raf = null; }
      else if (!raf) { last = performance.now(); raf = requestAnimationFrame(draw); }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  return <canvas ref={ref} className="zen-login-starfield" />;
}

function LoginCinematicBackdrop() {
  return (
    <div
      className="zen-login-cinematic-backdrop"
      data-testid="login-cinematic-backdrop"
      aria-hidden="true"
    >
      <img
        src={moonEarthCinematic}
        alt=""
        className="zen-login-cinematic-image"
        draggable="false"
      />
      <div className="zen-login-cinematic-shade" />
      <div className="zen-login-cinematic-light" />
    </div>
  );
}

/* Giriş saatine uygun Güneş/Ay-Dünya sistemi: 06:00-18:00 arası Güneş, gece
   Dünya + etrafında sürekli yörüngede dönen gerçekçi bir Ay — gökyüzünde
   doğudan (sol) batıya (sağ) yükselip-alçalan bir yay üzerinde, o anki saate
   göre sabit bir noktada konumlanır. */
function CelestialBody() {
  const { isDay, xPct, yPct } = useMemo(() => {
    const h = new Date().getHours() + new Date().getMinutes() / 60;
    const day = h >= 6 && h < 18;
    const t = day ? (h - 6) / 12 : (h >= 18 ? (h - 18) : (h + 6)) / 12;
    return { isDay: day, xPct: 8 + t * 84, yPct: 62 - Math.sin(t * Math.PI) * 46 };
  }, []);
  if (isDay) {
    return (
      <div
        className="zen-login-celestial zen-login-sun"
        style={{ left: `${xPct}%`, top: `${yPct}%` }}
        data-testid="login-celestial-body"
      />
    );
  }
  return (
    <div
      className="zen-login-celestial zen-login-night-scene"
      style={{ left: `${xPct}%`, top: `${yPct}%` }}
      data-testid="login-celestial-body"
    >
      <div className="zen-login-earth" />
      <div className="zen-login-orbit-ring" />
      <div className="zen-login-orbit">
        <div className="zen-login-orbit-moon">
          <span className="zen-moon-crater zen-moon-crater--1" />
          <span className="zen-moon-crater zen-moon-crater--2" />
          <span className="zen-moon-crater zen-moon-crater--3" />
        </div>
      </div>
    </div>
  );
}

const COUNTRY_CODES = [
  { code: "+90", label: "🇹🇷 Türkiye (+90)" },
  { code: "+49", label: "🇩🇪 Almanya (+49)" },
  { code: "+31", label: "🇳🇱 Hollanda (+31)" },
  { code: "+32", label: "🇧🇪 Belçika (+32)" },
  { code: "+33", label: "🇫🇷 Fransa (+33)" },
  { code: "+44", label: "🇬🇧 İngiltere (+44)" },
  { code: "+971", label: "🇦🇪 BAE (+971)" },
  { code: "+966", label: "🇸🇦 Suudi Arabistan (+966)" },
  { code: "+1", label: "🇺🇸 ABD/Kanada (+1)" },
  { code: "+7", label: "🇷🇺 Rusya (+7)" },
];

function LoginPage({ onLogin }) {
  const [mode, setMode] = useState("login"); // "login" | "forgot" | "reset" | "2fa" | "dealer"

  // GSAP: başlık harf harf canlanır (SplitText) — hareket azaltma tercihine saygılı
  // GÜVENLİK AĞI: animasyon yarıda kalsa bile yazı 2 sn içinde mutlaka görünür
  useGSAP(() => {
    const mm = gsap.matchMedia();
    const splits = [];
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const titles = gsap.utils.toArray("[data-anim='login-title']");
      titles.forEach((el) => {
        const split = SplitText.create(el, { type: "chars" });
        splits.push(split);
        gsap.fromTo(split.chars, { yPercent: 110, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.6, ease: "power3.out", stagger: 0.035, delay: 0.25, clearProps: "transform,opacity" });
      });
      gsap.fromTo("[data-anim='login-sub']", { y: 14, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, delay: 0.75, ease: "power2.out", clearProps: "transform,opacity" });
    });
    const safety = setTimeout(() => {
      splits.forEach((s) => { try { s.revert(); } catch (e) {} });
      document.querySelectorAll("[data-anim='login-sub']").forEach((el) => {
        el.style.opacity = ""; el.style.transform = "";
      });
    }, 2500);
    return () => { clearTimeout(safety); mm.revert(); };
  });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showDealerPw, setShowDealerPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [closing, setClosing] = useState(false);
  // Şifre sıfırlama
  const [resetToken, setResetToken] = useState("");
  const [resetInfo, setResetInfo] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Bayi girişi
  const [countryCode, setCountryCode] = useState("+90");
  const [phone, setPhone] = useState("");
  // 2FA
  const [pendingToken, setPendingToken] = useState("");
  const [twoFaMethods, setTwoFaMethods] = useState([]);
  const [twoFaMethod, setTwoFaMethod] = useState("");
  const [twoFaCode, setTwoFaCode] = useState("");
  const [emailCodeSent, setEmailCodeSent] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset_token");
    if (token) {
      setResetToken(token);
      setMode("reset");
    }
  }, []);

  const finishLogin = (data) => {
    if (data.token) localStorage.setItem("zen_token", data.token);
    toast.success(`Hoş geldiniz, ${data.name || data.username || data.email}`);
    setClosing(true);
    setTimeout(() => onLogin(data), 550);
  };

  // YEREL TEST KOLAYLIĞI: giriş ekranını otomatik atla — YALNIZCA test derlemesinde
  // (REACT_APP_DEV_AUTOLOGIN=true ile derlenir; VPS üretim derlemesinde bu bayrak
  // YOKTUR → otomatik giriş ASLA çalışmaz, giriş ekranı zorunlu kalır).
  const autoTried = useRef(false);
  useEffect(() => {
    const isLocalTest =
      process.env.REACT_APP_DEV_AUTOLOGIN === "true" ||
      ["localhost", "127.0.0.1"].includes(window.location.hostname);
    if (!isLocalTest) return;
    if (mode !== "login" || autoTried.current) return;
    autoTried.current = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.post("/auth/login", { username: "testadmin", password: "Test12345!" });
        if (data && !data.requires2FA) finishLogin(data);
        else setLoading(false);
      } catch {
        setLoading(false); // başarısızsa elle giriş açık kalır
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      if (data.requires2FA) {
        setPendingToken(data.pendingToken);
        setTwoFaMethods(data.methods || []);
        setTwoFaMethod((data.methods || [])[0] || "");
        setMode("2fa");
        setLoading(false);
        return;
      }
      finishLogin(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kullanıcı adı veya şifre hatalı");
      setLoading(false);
    }
  };

  const submitDealer = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/dealer-login", { countryCode, phone, password });
      finishLogin(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Telefon numarası veya şifre hatalı");
      setLoading(false);
    }
  };

  const sendEmailCode = async () => {
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/2fa/send-login-code", { pendingToken });
      setEmailCodeSent(true);
      toast.success("Kod e-postanıza gönderildi");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kod gönderilemedi");
    }
    setLoading(false);
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/verify-login", { pendingToken, method: twoFaMethod, code: twoFaCode });
      finishLogin(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kod hatalı");
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const resetUrlBase = `${window.location.origin}${window.location.pathname}`;
      const { data } = await api.post("/auth/forgot-password", { username, resetUrlBase });
      if (data.emailSent) {
        setResetInfo({ emailSent: true, maskedEmail: data.maskedEmail });
      } else if (data.token) {
        const resetLink = `${resetUrlBase}?reset_token=${data.token}`;
        setResetInfo({ token: data.token, link: resetLink });
      } else {
        setErr("Kullanıcı bulunamadı. Lütfen kullanıcı adınızı kontrol edin.");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPassword !== confirmPassword) {
      setErr("Şifreler eşleşmiyor");
      return;
    }
    if (newPassword.length < 6) {
      setErr("Şifre en az 6 karakter olmalıdır");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, new_password: newPassword });
      toast.success("Şifre başarıyla güncellendi!");
      window.history.pushState({}, "", window.location.pathname);
      setMode("login");
      setNewPassword("");
      setConfirmPassword("");
      setResetToken("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Şifre sıfırlama başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#020103]">
      <div className="zen-login-bg" />
      <LoginCinematicBackdrop />
      <div className="zen-login-nebula">
        <div className="zen-nebula-cloud zen-nebula-cloud--1" />
        <div className="zen-nebula-cloud zen-nebula-cloud--2" />
        <div className="zen-nebula-cloud zen-nebula-cloud--3" />
      </div>
      <LoginStarfield />

      <div className="zen-login-card-scale w-full max-w-md relative z-10">
        <div className={`zen-login-card-wrap ${closing ? "zen-login-exit" : "zen-login-rise"}`}>

          {/* ── GİRİŞ FORMU ── */}
          {mode === "login" && (
            <form onSubmit={submit} className="zen-login-card p-8 md:p-10">
              <div className="flex flex-col items-center text-center mb-8">
                <div className="zen-login-logo-float relative mb-6 w-32 h-32 flex items-center justify-center">
                  <div className="zen-login-logo-glow absolute inset-0 rounded-full" />
                  <div className="zen-login-logo-plate absolute inset-3 rounded-full overflow-hidden" />
                  <img src={logo} alt="ZEN" className="w-20 relative z-10 drop-shadow-[0_4px_14px_rgba(0,0,0,0.5)]" />
                  <div className="zen-login-logo-shadow" />
                </div>
                <h1 data-anim="login-title" className="text-3xl font-light text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" style={{ letterSpacing: "-0.02em" }}>Hoşgeldiniz</h1>
                <p data-anim="login-sub" className="text-[10px] uppercase text-cyan-100/55 mt-2.5 font-light drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]" style={{ letterSpacing: "0.24em" }}>
                  Mekanizmalı Perde Sistemleri
                </p>
              </div>

              <div className="flex gap-1.5 mb-6 bg-white/5 rounded-xl p-1">
                <button type="button" data-testid="login-tab-staff" className="flex-1 py-2 rounded-lg text-xs font-semibold bg-white/15 text-white">
                  Personel / Admin
                </button>
                <button type="button" data-testid="login-tab-dealer" onClick={() => { setMode("dealer"); setErr(""); }}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold text-white/50 hover:text-white/80 transition-colors">
                  Bayi Girişi
                </button>
              </div>

              <label className="block text-[11px] font-medium mb-1.5 text-white/70 uppercase tracking-wider">Kullanıcı Adı</label>
              <input
                data-testid="login-email"
                type="text" required value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="zen-login-input mb-4"
                placeholder="kullanici_adi"
                autoComplete="username"
              />
              <label className="block text-[11px] font-medium mb-1.5 text-white/70 uppercase tracking-wider">Şifre</label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPw ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="zen-login-input pr-12"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                  title={showPw ? "Şifreyi gizle" : "Şifreyi göster"}
                  aria-label="Şifreyi göster/gizle"
                  data-testid="login-password-toggle"
                >
                  {showPw ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {err && (
                <div className="mt-3 text-xs text-red-200 font-medium bg-red-500/15 border border-red-400/30 rounded-xl px-3 py-2" data-testid="login-error">
                  {err}
                </div>
              )}

              <button
                type="submit" disabled={loading}
                className="zen-login-btn mt-7"
                data-testid="login-submit"
              >
                {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
              </button>

              <div className="mt-5 text-center">
                <button type="button" onClick={() => { setMode("forgot"); setErr(""); setUsername(""); }}
                  className="text-[11px] text-cyan-300/60 hover:text-cyan-300/90 transition underline underline-offset-2">
                  Şifremi Unuttum
                </button>
              </div>
            </form>
          )}

          {/* ── BAYİ GİRİŞİ ── */}
          {mode === "dealer" && (
            <form onSubmit={submitDealer} className="zen-login-card p-8 md:p-10">
              <div className="flex flex-col items-center text-center mb-8">
                <div className="zen-login-logo-float relative mb-6 w-32 h-32 flex items-center justify-center">
                  <div className="zen-login-logo-glow absolute inset-0 rounded-full" />
                  <div className="zen-login-logo-plate absolute inset-3 rounded-full overflow-hidden" />
                  <img src={logo} alt="ZEN" className="w-20 relative z-10 drop-shadow-[0_4px_14px_rgba(0,0,0,0.5)]" />
                  <div className="zen-login-logo-shadow" />
                </div>
                <h1 data-anim="login-title" className="text-3xl font-light text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" style={{ letterSpacing: "-0.02em" }}>Bayi Girişi</h1>
              </div>

              <div className="flex gap-1.5 mb-6 bg-white/5 rounded-xl p-1">
                <button type="button" onClick={() => { setMode("login"); setErr(""); }}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold text-white/50 hover:text-white/80 transition-colors">
                  Personel / Admin
                </button>
                <button type="button" data-testid="login-tab-dealer-active" className="flex-1 py-2 rounded-lg text-xs font-semibold bg-white/15 text-white">
                  Bayi Girişi
                </button>
              </div>

              <label className="block text-[11px] font-medium mb-1.5 text-white/70 uppercase tracking-wider">Telefon Numarası</label>
              <div className="flex gap-2 mb-4">
                <select data-testid="dealer-login-country-code" value={countryCode} onChange={(e) => setCountryCode(e.target.value)}
                  className="zen-login-input !w-32">
                  {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
                <input
                  data-testid="dealer-login-phone"
                  type="tel" required value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="zen-login-input flex-1"
                  placeholder="5xx xxx xx xx"
                  autoComplete="tel"
                />
              </div>
              <label className="block text-[11px] font-medium mb-1.5 text-white/70 uppercase tracking-wider">Şifre</label>
              <div className="relative">
                <input
                  data-testid="dealer-login-password"
                  type={showDealerPw ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="zen-login-input pr-12"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowDealerPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                  title={showDealerPw ? "Şifreyi gizle" : "Şifreyi göster"}
                  aria-label="Şifreyi göster/gizle"
                  data-testid="dealer-login-password-toggle"
                >
                  {showDealerPw ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {err && (
                <div className="mt-3 text-xs text-red-200 font-medium bg-red-500/15 border border-red-400/30 rounded-xl px-3 py-2" data-testid="dealer-login-error">
                  {err}
                </div>
              )}
              <button type="submit" disabled={loading} className="zen-login-btn mt-7" data-testid="dealer-login-submit">
                {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
              </button>
            </form>
          )}

          {/* ── 2FA DOĞRULAMA ── */}
          {mode === "2fa" && (
            <form onSubmit={submit2FA} className="zen-login-card p-8 md:p-10">
              <div className="flex flex-col items-center text-center mb-8">
                <div className="zen-login-logo-float relative mb-6 w-32 h-32 flex items-center justify-center">
                  <div className="zen-login-logo-glow absolute inset-0 rounded-full" />
                  <div className="zen-login-logo-plate absolute inset-3 rounded-full overflow-hidden" />
                  <img src={logo} alt="ZEN" className="w-20 relative z-10 drop-shadow-[0_4px_14px_rgba(0,0,0,0.5)]" />
                  <div className="zen-login-logo-shadow" />
                </div>
                <h1 className="text-2xl font-light text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">2 Adımlı Doğrulama</h1>
              </div>

              {twoFaMethods.length > 1 && (
                <div className="flex gap-1.5 mb-5 bg-white/5 rounded-xl p-1">
                  {twoFaMethods.includes("totp") && (
                    <button type="button" data-testid="twofa-method-totp" onClick={() => { setTwoFaMethod("totp"); setTwoFaCode(""); }}
                      className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${twoFaMethod === "totp" ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80"}`}>
                      Authenticator
                    </button>
                  )}
                  {twoFaMethods.includes("email") && (
                    <button type="button" data-testid="twofa-method-email" onClick={() => { setTwoFaMethod("email"); setTwoFaCode(""); }}
                      className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${twoFaMethod === "email" ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80"}`}>
                      E-posta
                    </button>
                  )}
                </div>
              )}

              {twoFaMethod === "email" && (
                <button type="button" data-testid="twofa-send-email-code" onClick={sendEmailCode} disabled={loading}
                  className="w-full mb-4 text-xs font-semibold text-cyan-300 border border-cyan-400/30 rounded-xl py-2.5 hover:bg-cyan-400/10 transition-colors">
                  {emailCodeSent ? "Kodu Tekrar Gönder" : "E-postama Kod Gönder"}
                </button>
              )}

              <label className="block text-[11px] font-medium mb-1.5 text-white/70 uppercase tracking-wider">
                {twoFaMethod === "totp" ? "Authenticator Kodu" : "E-posta Kodu"}
              </label>
              <input
                data-testid="twofa-code-input"
                type="text" required value={twoFaCode} maxLength={6}
                onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, ""))}
                className="zen-login-input text-center tracking-[0.5em] font-mono text-lg"
                placeholder="000000"
              />
              {err && (
                <div className="mt-3 text-xs text-red-200 font-medium bg-red-500/15 border border-red-400/30 rounded-xl px-3 py-2" data-testid="twofa-error">
                  {err}
                </div>
              )}
              <button type="submit" disabled={loading} className="zen-login-btn mt-7" data-testid="twofa-submit">
                {loading ? "Doğrulanıyor..." : "Doğrula ve Giriş Yap"}
              </button>
              <div className="mt-5 text-center">
                <button type="button" onClick={() => { setMode("login"); setErr(""); setTwoFaCode(""); }}
                  className="text-[11px] text-cyan-300/60 hover:text-cyan-300/90 transition underline underline-offset-2">
                  ← Girişe Dön
                </button>
              </div>
            </form>
          )}

          {/* ── ŞİFREMİ UNUTTUM ── */}
          {mode === "forgot" && (
            <div className="zen-login-card p-8 md:p-10">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-400/20 flex items-center justify-center mb-4">
                  <svg className="w-7 h-7 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white">Şifre Sıfırlama</h2>
                <p className="text-xs text-white/50 mt-1">Kullanıcı adınızı girin</p>
              </div>

              {!resetInfo ? (
                <form onSubmit={submitForgot}>
                  <label className="block text-[11px] font-medium mb-1.5 text-white/70 uppercase tracking-wider">Kullanıcı Adı</label>
                  <input
                    data-testid="forgot-username-input"
                    type="text" required value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="zen-login-input mb-4"
                    placeholder="kullanici_adi"
                  />
                  {err && (
                    <div className="mt-2 text-xs text-red-200 bg-red-500/15 border border-red-400/30 rounded-xl px-3 py-2">
                      {err}
                    </div>
                  )}
                  <button type="submit" disabled={loading} className="zen-login-btn mt-5">
                    {loading ? "Oluşturuluyor..." : "Sıfırlama Bağlantısı Oluştur"}
                  </button>
                </form>
              ) : resetInfo.emailSent ? (
                <div className="space-y-4">
                  <div className="bg-green-500/10 border border-green-400/20 rounded-xl p-4 text-center" data-testid="forgot-email-sent-panel">
                    <div className="text-xs text-green-300 font-semibold mb-2">E-posta Gönderildi</div>
                    <p className="text-[11px] text-white/60">
                      <span className="font-mono text-cyan-300/80">{resetInfo.maskedEmail}</span> adresine bir sıfırlama
                      bağlantısı gönderdik (1 saat geçerli). Gelen kutunuzu kontrol edin.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-500/10 border border-green-400/20 rounded-xl p-4">
                    <div className="text-xs text-green-300 font-semibold mb-2">Sıfırlama Bağlantısı Oluşturuldu</div>
                    <p className="text-[11px] text-white/60 mb-3">Bu bağlantıyı kopyalayıp tarayıcıda açın (1 saat geçerli):</p>
                    <div className="bg-black/30 rounded-lg p-2.5 break-all text-[10px] font-mono text-cyan-300/80">
                      {resetInfo.link}
                    </div>
                    <button
                      data-testid="forgot-copy-link-btn"
                      onClick={() => { navigator.clipboard.writeText(resetInfo.link); toast.success("Bağlantı kopyalandı"); }}
                      className="mt-2 w-full text-xs py-1.5 rounded-lg bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 transition"
                    >
                      Bağlantıyı Kopyala
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-5 text-center">
                <button type="button" onClick={() => { setMode("login"); setErr(""); setResetInfo(null); }}
                  className="text-[11px] text-white/40 hover:text-white/70 transition">
                  ← Girişe Dön
                </button>
              </div>
            </div>
          )}

          {/* ── ŞİFRE SIFIRLAMA ── */}
          {mode === "reset" && (
            <form onSubmit={submitReset} className="zen-login-card p-8 md:p-10">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-400/20 flex items-center justify-center mb-4">
                  <svg className="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white">Yeni Şifre Belirle</h2>
              </div>

              <label className="block text-[11px] font-medium mb-1.5 text-white/70 uppercase tracking-wider">Yeni Şifre</label>
              <input
                data-testid="reset-new-password"
                type="password" required value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="zen-login-input mb-4"
                placeholder="En az 6 karakter"
                autoComplete="new-password"
              />
              <label className="block text-[11px] font-medium mb-1.5 text-white/70 uppercase tracking-wider">Şifre Tekrar</label>
              <input
                data-testid="reset-confirm-password"
                type="password" required value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="zen-login-input"
                placeholder="Şifreyi tekrar girin"
                autoComplete="new-password"
              />
              {err && (
                <div className="mt-3 text-xs text-red-200 bg-red-500/15 border border-red-400/30 rounded-xl px-3 py-2">
                  {err}
                </div>
              )}
              <button type="submit" disabled={loading} className="zen-login-btn mt-7">
                {loading ? "Güncelleniyor..." : "Şifreyi Güncelle"}
              </button>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}


export default LoginPage;
